import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { parseFile } from '../utils/pdfParser';
import aiService from '../services/aiService';
import PaymentModal from './PaymentModal';
import OptimizedResumeResults from './OptimizedResumeResults';
import DragDropUpload from './DragDropUpload';
import {
  FileText,
  Upload,
  ClipboardList,
  Brain,
  AlertTriangle,
  TrendingUp,
  Star,
  Lock,
  Zap,
  Target,
  Download,
  Eye,
  ArrowRight,
  CheckCircle,
  XCircle,
  CreditCard,
  Sparkles
} from 'lucide-react';

const EnhancedFreemiumResumeApp = () => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeText, setResumeText] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [uploadMethod, setUploadMethod] = useState('text');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [fullResult, setFullResult] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [userSubscription, setUserSubscription] = useState(null);
  const [error, setError] = useState('');

  // Security: Check for valid optimization session on component mount
  useEffect(() => {
    const checkOptimizationSession = () => {
      const sessionData = localStorage.getItem('shortlistly_optimization_session');
      if (sessionData) {
        try {
          const session = JSON.parse(sessionData);
          
          // Verify session validity (24 hours)
          if (Date.now() - session.timestamp > 24 * 60 * 60 * 1000) {
            console.log('🔒 Optimization session expired, clearing...');
            localStorage.removeItem('shortlistly_optimization_session');
            return;
          }
          
          // Check if session has payment verification
          if (session.paymentVerified && session.resumeText && session.jobDescription) {
            console.log('✅ Valid optimization session found');
            
            // If user is not signed in but has valid session, show reminder
            if (!user) {
              setError('✅ Optimization completed successfully! Your resume has been optimized and templates are ready for download.');
            }
          }
        } catch (error) {
          console.error('Error parsing optimization session:', error);
          localStorage.removeItem('shortlistly_optimization_session');
        }
      }
    };
    
    checkOptimizationSession();
    
    // Load user subscription when user is available
    if (user && user.uid) {
      loadUserSubscription();
    }
  }, [user]);

  // Add session cleanup on page unload - Modified to call resetApp
  useEffect(() => {
    // Removed the call to resetApp() here.
    // The browser's native beforeunload confirmation will prevent unload if cancelled.
    // If the page does unload, the component will remount and naturally reset.
    // The OptimizedResumeResults.js component also handles a beforeunload warning.
    const handleBeforeUnload = (e) => {
      // This listener is primarily for showing a warning.
      // The actual reset will happen naturally on component unmount/remount
      // if the user confirms the navigation/refresh.
      // No explicit resetApp() call needed here.
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []); // Empty dependency array as resetApp is stable

  const loadUserSubscription = async () => {
    try {
      if (!user || !user.uid) {
        console.warn('User or user.uid is null, cannot load subscription');
        return;
      }
      const response = await aiService.getUserSubscription(user.uid);
      setUserSubscription(response);
    } catch (error) {
      console.error('Failed to load subscription:', error);
    }
  };

  const handleFileUpload = async (file) => {
    setResumeFile(file);
    setError('🔄 Processing file...');
    
    try {
      const extractedText = await parseFile(file);
      if (extractedText && extractedText.length > 20) {
        setResumeText(extractedText);
        setError('');
        
        // Switch to text view to show the extracted content
        setUploadMethod('text');
        
        // Show success message briefly
        setError('✅ PDF processed successfully! Text extracted and ready for analysis.');
        setTimeout(() => setError(''), 3000);
        
        // Focus the textarea after a brief delay to allow DOM update
        setTimeout(() => {
          const textarea = document.querySelector('textarea');
          if (textarea) {
            textarea.focus();
            textarea.scrollTop = 0; // Scroll to top of extracted text
          }
        }, 100);
        
      } else {
        throw new Error('File appears to be empty or contains no readable text.');
      }
    } catch (error) {
      setError(`❌ ${error.message} Please paste your resume text instead.`);
      setResumeFile(null);
      setUploadMethod('text');
      throw error; // Re-throw to let DragDropUpload handle the error state
    }
  };

  const analyzeResume = async () => {
    if (!resumeText.trim() || !jobDescription.trim()) {
      setError('Please provide both resume text and job description');
      return;
    }

    setIsAnalyzing(true);
    setError('');
    
    try {
      console.log('🔍 Starting resume analysis...');
      
      const result = await aiService.getResumePreview(
        resumeText, 
        jobDescription, 
        user?.uid || `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      );
      
      console.log('📊 Analysis result:', result);
      
      
      setPreviewResult(result);
      setCurrentStep(2);
    } catch (error) {
      console.error('❌ Analysis failed:', error);
      setError(error.message || 'Failed to analyze resume. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleUpgrade = () => {
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    
    console.log('🎉 Payment success handler called');
    
    // Generate a unique session ID to track this optimization
    const optimizationSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Force proceed with optimization regardless of user state
    // The payment was verified successfully, so we can proceed
    const proceedWithOptimization = async () => {
      try {
        console.log('🚀 Proceeding with full optimization after payment...');
        setIsAnalyzing(true);
        setCurrentStep(3);

        // Create a unique identifier for this optimization session
        const userIdForOptimization = user?.uid || optimizationSessionId;
        
        // Store optimization context in localStorage for recovery
        const optimizationContext = {
          sessionId: optimizationSessionId,
          userId: userIdForOptimization,
          resumeText: resumeText,
          jobDescription: jobDescription,
          timestamp: Date.now(),
          paymentVerified: true
        };
        
        // Store securely with expiration (24 hours)
        localStorage.setItem('shortlistly_optimization_session', JSON.stringify(optimizationContext));
        
        // Set expiration cleanup
        setTimeout(() => {
          localStorage.removeItem('shortlistly_optimization_session');
        }, 24 * 60 * 60 * 1000); // 24 hours

        // Use stored resume data to proceed with optimization
        const fullOptimization = await aiService.optimizeResume(
          resumeText,
          jobDescription,
          userIdForOptimization,
          '' // targetIndustry
        );

        console.log('✅ Full optimization completed:', fullOptimization);
        
        // Add session info to result for security tracking
        const secureResult = {
          ...fullOptimization,
          sessionId: optimizationSessionId,
          optimizationId: fullOptimization.id || `opt_${Date.now()}`,
          downloadAvailable: true,
          downloadExpiry: Date.now() + (24 * 60 * 60 * 1000) // 24 hours
        };
        
        setFullResult(secureResult);
        setIsAnalyzing(false);
        
        // Clear sensitive data after successful optimization
        setTimeout(() => {
          setResumeText('');
          setJobDescription('');
        }, 5000); // Clear after 5 seconds
        
      } catch (error) {
        console.error('❌ Full optimization failed:', error);
        setError('Optimization failed: ' + error.message);
        setIsAnalyzing(false);
        
        // Clear session on error
        localStorage.removeItem('shortlistly_optimization_session');
      }
    };

    // Proceed immediately since payment was verified
    proceedWithOptimization();
  };

  const handleDownload = async (format, template = 'professional') => {
    try {
      console.log('🔍 Download request:', { format, template, fullResult });
      
      if (!fullResult || !fullResult.optimized_resume) {
        setError('No optimized resume available for download');
        return;
      }

      // Create download using the optimized resume text directly
      const resumeContent = fullResult.optimized_resume;
      const fileName = `optimized_resume_${template}.${format}`;
      
      if (format === 'txt') {
        // For TXT, just download the plain text
        const blob = new Blob([resumeContent], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        console.log('✅ TXT download completed successfully');
        
        // Removed call to handleDownloadComplete();
        return;
      }

      // For PDF and DOCX, we need to call the backend to convert
      // But use a different endpoint that accepts the resume content directly
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/resume/convert?format=${format}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': format === 'pdf' 
              ? 'application/pdf' 
              : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          },
          body: JSON.stringify({
            resume_content: resumeContent,
            template: template,
            structured_data: fullResult.structured_resume_data || {}
          })
        }
      );

      console.log('📥 Convert response status:', response.status);

      if (!response.ok) {
        // Fallback: if convert endpoint doesn't exist, try original method
        if (response.status === 404) {
          console.log('⚠️ Convert endpoint not found, using fallback TXT download');
          const blob = new Blob([resumeContent], { type: 'text/plain' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `optimized_resume_${template}.txt`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          console.log('✅ Fallback TXT download completed successfully');
          
          // Removed call to handleDownloadComplete();
          return;
        }
        
        const errorText = await response.text();
        console.error('❌ Convert failed:', response.status, errorText);
        throw new Error(`Convert failed: ${response.status} - ${errorText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      console.log('✅ Download completed successfully');
      
      // Removed call to handleDownloadComplete();
      
    } catch (error) {
      console.error('❌ Download failed:', error);
      setError(`Download failed: ${error.message}. Please try again.`);
    }
  };

  // Removed handleDownloadComplete function

  const resetApp = () => {
    console.log('🔄 Resetting app to initial state...');
    
    // Clear all state variables
    setCurrentStep(1);
    setResumeFile(null);
    setResumeText('');
    setJobDescription('');
    setPreviewResult(null);
    setFullResult(null);
    setError('');
    
    // Clear any optimization session data
    localStorage.removeItem('shortlistly_optimization_session');
    localStorage.removeItem('shortlistly_download_session');
    
    console.log('✅ App reset completed - ready for fresh session');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    });
  };

  // Check if user has paid access
  const hasPaidAccess = userSubscription && ['basic', 'premium'].includes(userSubscription.plan_id);

  return (
    <section id="freemium-app" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            AI Resume Optimizer
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Get instant AI analysis of your resume and see exactly what's blocking your job interviews
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center mb-12">
          {[1, 2, 3].map((step) => (
            <div key={step} className="flex items-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition-all duration-300 ${
                currentStep >= step 
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg' 
                  : 'bg-slate-200 text-slate-500'
              }`}>
                {step}
              </div>
              {step < 3 && (
                <div className={`w-16 h-1 mx-4 transition-all duration-300 ${
                  currentStep > step ? 'bg-gradient-to-r from-blue-600 to-purple-600' : 'bg-slate-200'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Error/Success Display */}
        {error && (
          <div className={`mb-6 p-4 border rounded-xl text-center font-medium ${
            error.includes('✅') || error.includes('successfully') 
              ? 'bg-green-50 border-green-200 text-green-700' 
              : error.includes('🔄') || error.includes('Processing')
              ? 'bg-blue-50 border-blue-200 text-blue-700'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}>
            {error}
          </div>
        )}

        <AnimatePresence mode="wait">
          {/* Step 1: Input Resume and Job Description */}
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid lg:grid-cols-2 gap-8"
            >
              <EnhancedResumeInputSection
                uploadMethod={uploadMethod}
                setUploadMethod={setUploadMethod}
                resumeText={resumeText}
                setResumeText={setResumeText}
                handleFileUpload={handleFileUpload}
                resumeFile={resumeFile}
                setResumeFile={setResumeFile}
              />
              
              <JobDescriptionSection
                jobDescription={jobDescription}
                setJobDescription={setJobDescription}
                onAnalyze={analyzeResume}
                disabled={!resumeText.trim() || !jobDescription.trim()}
                isAnalyzing={isAnalyzing}
              />
            </motion.div>
          )}

          {/* Step 2: Preview Results with Conversion */}
          {currentStep === 2 && previewResult && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <PreviewResultsSection
                result={previewResult}
                onUpgrade={handleUpgrade}
                onReset={resetApp}
                hasPaidAccess={hasPaidAccess}
                onProceedFull={handleDownload}
              />
            </motion.div>
          )}

          {/* Step 3: Full Results (Paid) */}
          {currentStep === 3 && fullResult && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <OptimizedResumeResults
                fullResult={fullResult}
                onDownload={handleDownload}
              />
            </motion.div>
          )}

          {/* Loading States */}
          {isAnalyzing && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <Brain className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-spin" />
              <h3 className="text-2xl font-bold text-slate-900 mb-2">
                {currentStep === 2 ? 'Analyzing Your Resume...' : 'Optimizing with AI...'}
              </h3>
              <p className="text-slate-600">
                {currentStep === 2 
                  ? 'Our AI is scanning for critical issues and missing keywords'
                  : 'Creating your optimized resume with advanced AI technology'
                }
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Payment Modal */}
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={handlePaymentSuccess}
          userId={user?.uid}
        />
      </div>
    </section>
  );
};

// Enhanced Resume Input Section Component with Drag & Drop
const EnhancedResumeInputSection = ({ 
  uploadMethod, 
  setUploadMethod, 
  resumeText, 
  setResumeText, 
  handleFileUpload,
  resumeFile,
  setResumeFile
}) => (
  <div className="bg-white rounded-3xl p-8 shadow-xl border-2 border-slate-100">
    <div className="flex items-center mb-6">
      <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mr-4">
        <FileText className="w-6 h-6 text-white" />
      </div>
      <h3 className="text-2xl font-bold text-slate-900">Your Resume</h3>
    </div>

    {/* Upload Method Toggle */}
    <div className="flex mb-6 bg-slate-100 rounded-2xl p-2">
      <button
        onClick={() => setUploadMethod('text')}
        className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
          uploadMethod === 'text' 
            ? 'bg-white text-blue-600 shadow-md' 
            : 'text-slate-600'
        }`}
      >
        <ClipboardList className="w-5 h-5 inline mr-2" />
        Paste Text
      </button>
      <button
        onClick={() => setUploadMethod('file')}
        className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
          uploadMethod === 'file' 
            ? 'bg-white text-blue-600 shadow-md' 
            : 'text-slate-600'
        }`}
      >
        <Upload className="w-5 h-5 inline mr-2" />
        Upload File
      </button>
    </div>

    {uploadMethod === 'file' ? (
      <DragDropUpload 
        onFileUpload={handleFileUpload}
        acceptedTypes=".pdf,.doc,.docx,.txt"
        maxSize={10 * 1024 * 1024} // 10MB
      />
    ) : (
      <div className="space-y-3">
        {/* Show file upload success indicator */}
        {resumeFile && (
          <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-xl">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              <span className="text-green-700 font-medium">
                📄 {resumeFile.name} processed successfully
              </span>
            </div>
            <button
              onClick={() => {
                setResumeFile(null);
                setResumeText('');
                setUploadMethod('file');
              }}
              className="text-green-600 hover:text-green-800 font-medium"
            >
              Upload Different File
            </button>
          </div>
        )}
        
        <textarea
          value={resumeText}
          onChange={(e) => setResumeText(e.target.value)}
          className={`w-full h-64 p-4 border rounded-2xl focus:ring-2 focus:border-transparent resize-none transition-all ${
            resumeFile 
              ? 'border-green-300 bg-green-50 focus:ring-green-500' 
              : 'border-slate-300 focus:ring-blue-500'
          }`}
          placeholder={resumeFile ? "Text extracted from your uploaded file. You can edit it here..." : "Paste your complete resume here..."}
        />
      </div>
    )}
  </div>
);

// Job Description Section Component (unchanged)
const JobDescriptionSection = ({ 
  jobDescription, 
  setJobDescription, 
  onAnalyze, 
  disabled, 
  isAnalyzing
}) => (
  <div className="bg-white rounded-3xl p-8 shadow-xl border-2 border-slate-100">
    <div className="flex items-center mb-6">
      <div className="w-12 h-12 bg-gradient-to-r from-green-600 to-blue-600 rounded-2xl flex items-center justify-center mr-4">
        <Target className="w-6 h-6 text-white" />
      </div>
      <h3 className="text-2xl font-bold text-slate-900">Job Description</h3>
    </div>

    <textarea
      value={jobDescription}
      onChange={(e) => setJobDescription(e.target.value)}
      className="w-full h-64 p-4 border border-slate-300 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none mb-6"
      placeholder="Paste the job description you're targeting here..."
    />


    <button
      onClick={onAnalyze}
      disabled={disabled || isAnalyzing}
      className="w-full flex items-center justify-center px-6 py-4 bg-gradient-to-r from-green-600 to-blue-600 text-white font-bold text-lg rounded-2xl hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {isAnalyzing ? (
        <>
          <Brain className="w-6 h-6 mr-3 animate-spin" />
          Analyzing with AI...
        </>
      ) : (
        <>
          <Sparkles className="w-6 h-6 mr-3" />
          Analyze My Resume FREE
        </>
      )}
    </button>
  </div>
);

// Preview Results Section Component (unchanged from original)
const PreviewResultsSection = ({ 
  result, 
  onUpgrade, 
  onReset, 
  hasPaidAccess,
  onProceedFull 
}) => (
  <div className="space-y-8">
    {/* ATS Score Display */}
    <div className="bg-white rounded-3xl p-8 shadow-xl text-center">
      <div className="mb-6">
        <div className="text-6xl font-bold text-red-500 mb-2">{result.basic_ats_score}%</div>
        <div className="text-xl text-slate-600">ATS Compatibility Score</div>
        <div className="text-red-600 font-semibold mt-2">⚠️ Needs Immediate Improvement</div>
      </div>
    </div>

    {/* Critical Issues (Guilt-driven) */}
    <div className="bg-white rounded-3xl p-8 shadow-xl">
      <div className="flex items-center mb-6">
        <AlertTriangle className="w-8 h-8 text-red-500 mr-3" />
        <h3 className="text-2xl font-bold text-slate-900">Critical Issues Found</h3>
      </div>
      
      <div className="space-y-4 mb-6">
        {result.critical_issues.map((issue, index) => (
          <div key={index} className="flex items-start p-4 bg-red-50 border border-red-200 rounded-xl">
            <XCircle className="w-5 h-5 text-red-500 mr-3 mt-0.5" />
            <span className="text-red-700">{issue}</span>
          </div>
        ))}
      </div>

      {/* Guilt Messages */}
      <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl p-6 text-white">
        <h4 className="text-xl font-bold mb-4">Why This Matters:</h4>
        <div className="space-y-2">
          {result.guilt_messages.map((message, index) => (
            <div key={index} className="flex items-start">
              <span className="mr-2">•</span>
              <span>{message}</span>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Urgency Stats */}
    <div className="grid md:grid-cols-2 gap-6">
      <div className="bg-white rounded-3xl p-6 shadow-xl">
        <h4 className="text-lg font-bold text-slate-900 mb-4">Market Reality</h4>
        <div className="space-y-3">
          {Object.entries(result.urgency_stats).map(([key, value], index) => (
            <div key={index} className="flex items-center">
              <TrendingUp className="w-5 h-5 text-red-500 mr-3" />
              <span className="text-slate-700">{value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-3xl p-6 shadow-xl">
        <h4 className="text-lg font-bold text-slate-900 mb-4">What You're Missing</h4>
        <div className="space-y-2">
          {result.missing_keywords.slice(0, 6).map((keyword, index) => (
            <div key={index} className="flex items-center">
              <Lock className="w-4 h-4 text-orange-500 mr-2" />
              <span className="text-slate-600">{keyword}</span>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Improvement Preview */}
    <div className="bg-white rounded-3xl p-8 shadow-xl">
      <h3 className="text-2xl font-bold text-slate-900 mb-6">How AI Will Transform Your Resume</h3>
      <div className="space-y-4 mb-6">
        {result.improvement_preview.map((improvement, index) => (
          <div key={index} className="flex items-start p-4 bg-blue-50 border border-blue-200 rounded-xl">
            {index < 3 ? (
              <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-0.5" />
            ) : (
              <Lock className="w-5 h-5 text-orange-500 mr-3 mt-0.5" />
            )}
            <span className={index < 3 ? "text-blue-700" : "text-orange-700 font-semibold"}>
              {improvement}
            </span>
          </div>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4">
        {hasPaidAccess ? (
          <button
            onClick={onProceedFull}
            className="flex-1 flex items-center justify-center px-6 py-4 bg-gradient-to-r from-green-600 to-blue-600 text-white font-bold rounded-2xl hover:shadow-lg transition-all"
          >
            <Zap className="w-5 h-5 mr-2" />
            Get Full AI Optimization
          </button>
        ) : (
          <button
            onClick={onUpgrade}
            className="flex-1 flex items-center justify-center px-6 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold rounded-2xl hover:shadow-lg transition-all"
          >
            <CreditCard className="w-5 h-5 mr-2" />
            Unlock Full Optimization - ₹199
          </button>
        )}
        
        <button
          onClick={onReset}
          className="px-6 py-4 border-2 border-slate-300 text-slate-700 font-semibold rounded-2xl hover:bg-slate-50 transition-all"
        >
          Try Another Resume
        </button>
      </div>
    </div>
  </div>
);

// Full Results Section Component (unchanged from original)
const FullResultsSection = ({ result, onReset, onCopy }) => (
  <div className="space-y-8">
    {/* Success Header */}
    <div className="text-center bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-3xl p-8">
      <CheckCircle className="w-16 h-16 mx-auto mb-4" />
      <h2 className="text-3xl font-bold mb-2">Resume Optimized Successfully!</h2>
      <p className="text-xl">Your resume is now optimized for maximum ATS compatibility</p>
    </div>

    {/* Results Grid */}
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Optimized Resume */}
      <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-slate-900">Optimized Resume</h4>
          <div className="flex gap-2">
            <button
              onClick={() => onCopy(result.optimized_resume)}
              className="flex items-center px-3 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
            >
              <Eye className="w-4 h-4 mr-1" />
              Copy
            </button>
            <button className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              <Download className="w-4 h-4 mr-1" />
              Download PDF
            </button>
          </div>
        </div>
        <div className="bg-slate-50 rounded-xl p-4 max-h-96 overflow-y-auto">
          <pre className="whitespace-pre-wrap text-sm text-slate-700">
            {result.optimized_resume}
          </pre>
        </div>
      </div>

      {/* Stats and Improvements */}
      <div className="space-y-6">
        {/* ATS Score Improvement */}
        <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
          <h4 className="text-lg font-semibold text-slate-900 mb-4">ATS Score</h4>
          <div className="text-4xl font-bold text-green-600 mb-2">{result.ats_score}%</div>
          <div className="text-sm text-slate-600">
            Improved from {result.original_score}%
          </div>
          <div className="text-green-600 font-semibold mt-2">
            +{result.ats_score - result.original_score}% improvement!
          </div>
        </div>

        {/* Keywords */}
        <div className="bg-white rounded-2xl p-6 shadow-lg">
          <h4 className="text-lg font-semibold text-slate-900 mb-4">Keywords Added</h4>
          <div className="space-y-2">
            {result.keyword_matches?.slice(0, 6).map((keyword, index) => (
              <div key={index} className="flex items-center">
                <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                <span className="text-sm text-slate-700">{keyword}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={onReset}
            className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all"
          >
            <ArrowRight className="w-4 h-4 mr-2" />
            Optimize Another Resume
          </button>
        </div>
      </div>
    </div>
  </div>
);

export default EnhancedFreemiumResumeApp;
